import {
  DEFAULT_BODY_FONT,
  DEFAULT_HEADING_FONT,
  isEditorFontId,
} from "../../editor/fonts";
import { just, nothing, type Maybe } from "../maybe";
import { err, ok, valuesOk, type Result } from "../result";
import {
  defaultZwChapterHeadingStyle,
  defaultZwConfig,
  defaultZwCopyrightPage,
  defaultZwDedicationPage,
  defaultZwGoals,
  defaultZwMetadata,
  defaultZwPageChrome,
  defaultZwParagraphFormatting,
  defaultZwSceneFormatting,
  defaultZwStatusBar,
} from "./defaults";
import { normalizeManuscript } from "./scenes";
import {
  normalizeChapterNumbering,
  normalizeChapterOverrides,
} from "../chapterNumbering";
import {
  CHAPTER_HEADING_SCALE_MAX,
  CHAPTER_HEADING_SCALE_MIN,
  CHAPTER_HEADING_SPACE_MAX,
  CHAPTER_HEADING_SPACE_MIN,
  DEFAULT_PRINT_SIZE,
  isZwChapterTitleAlignment,
  isZwChapterTitleImagePosition,
  isZwDatabaseFieldKind,
  isZwPageNumberPlacement,
  isZwFirstParagraph,
  isZwOpeningInitial,
  isZwOpeningLeadIn,
  isZwParagraphStyle,
  isZwPrintSize,
  isZwSceneBreakStyle,
} from "./types";
import type {
  ZwChapterHeadingStyle,
  ZwCloudDivergence,
  ZwCloudSync,
  ZwConfig,
  ZwCopyrightCredit,
  ZwCopyrightPage,
  ZwDatabaseFieldDef,
  ZwDatabaseRelationPair,
  ZwDedicationPage,
  ZwGoals,
  ZwMetadata,
  ZwPageChrome,
  ZwParagraphFormatting,
  ZwSceneFormatting,
  ZwStatusBar,
} from "./types";

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function normalizeZwPageChrome(raw: unknown): ZwPageChrome {
  const base = defaultZwPageChrome();
  if (raw === null || typeof raw !== "object") return base;
  const obj = raw as Record<string, unknown>;
  return {
    showTitle:
      typeof obj.showTitle === "boolean" ? obj.showTitle : base.showTitle,
    showAuthor:
      typeof obj.showAuthor === "boolean" ? obj.showAuthor : base.showAuthor,
    pageNumber:
      typeof obj.pageNumber === "string" &&
      isZwPageNumberPlacement(obj.pageNumber)
        ? obj.pageNumber
        : base.pageNumber,
  };
}

export function normalizeZwParagraphFormatting(
  raw: unknown,
): ZwParagraphFormatting {
  const base = defaultZwParagraphFormatting();
  if (raw === null || typeof raw !== "object") return base;
  const obj = raw as Record<string, unknown>;
  return {
    style:
      typeof obj.style === "string" && isZwParagraphStyle(obj.style)
        ? obj.style
        : base.style,
    firstParagraph:
      typeof obj.firstParagraph === "string" &&
      isZwFirstParagraph(obj.firstParagraph)
        ? obj.firstParagraph
        : base.firstParagraph,
    openingInitial:
      typeof obj.openingInitial === "string" &&
      isZwOpeningInitial(obj.openingInitial)
        ? obj.openingInitial
        : base.openingInitial,
    openingLeadIn:
      typeof obj.openingLeadIn === "string" &&
      isZwOpeningLeadIn(obj.openingLeadIn)
        ? obj.openingLeadIn
        : base.openingLeadIn,
  };
}

function clampChapterHeadingNumber(
  value: unknown,
  min: number,
  max: number,
  fallback: number,
): number {
  if (
    typeof value !== "number" ||
    Number.isNaN(value) ||
    Number.isFinite(value) === false
  ) {
    return fallback;
  }
  if (value < min || value > max) return fallback;
  return value;
}

export function normalizeZwChapterHeadingStyle(
  raw: unknown,
): ZwChapterHeadingStyle {
  const base = defaultZwChapterHeadingStyle();
  if (raw === null || typeof raw !== "object") return base;
  const obj = raw as Record<string, unknown>;
  return {
    alignment:
      typeof obj.alignment === "string" &&
      isZwChapterTitleAlignment(obj.alignment)
        ? obj.alignment
        : base.alignment,
    spaceBefore: clampChapterHeadingNumber(
      obj.spaceBefore,
      CHAPTER_HEADING_SPACE_MIN,
      CHAPTER_HEADING_SPACE_MAX,
      base.spaceBefore,
    ),
    spaceAfter: clampChapterHeadingNumber(
      obj.spaceAfter,
      CHAPTER_HEADING_SPACE_MIN,
      CHAPTER_HEADING_SPACE_MAX,
      base.spaceAfter,
    ),
    scale: clampChapterHeadingNumber(
      obj.scale,
      CHAPTER_HEADING_SCALE_MIN,
      CHAPTER_HEADING_SCALE_MAX,
      base.scale,
    ),
    image: typeof obj.image === "string" ? obj.image : base.image,
    imagePosition:
      typeof obj.imagePosition === "string" &&
      isZwChapterTitleImagePosition(obj.imagePosition)
        ? obj.imagePosition
        : base.imagePosition,
  };
}

export function normalizeZwSceneFormatting(raw: unknown): ZwSceneFormatting {
  const base = defaultZwSceneFormatting();
  if (raw === null || typeof raw !== "object") return base;
  const obj = raw as Record<string, unknown>;
  return {
    showSceneTitles:
      typeof obj.showSceneTitles === "boolean"
        ? obj.showSceneTitles
        : base.showSceneTitles,
    breakStyle:
      typeof obj.breakStyle === "string" && isZwSceneBreakStyle(obj.breakStyle)
        ? obj.breakStyle
        : base.breakStyle,
    breakCustomText:
      typeof obj.breakCustomText === "string"
        ? obj.breakCustomText
        : base.breakCustomText,
  };
}

/**
 * Parse one copyright credit. Empty / invalid shapes are Nothing (skipped by
 * `normalizeZwCopyrightPage`). First Maybe adopter in zwConfig.
 */
export function normalizeZwCopyrightCredit(
  raw: unknown,
): Maybe<ZwCopyrightCredit> {
  if (raw === null || typeof raw !== "object") return nothing;
  const obj = raw as Record<string, unknown>;
  const role = asString(obj.role).trim();
  const name = asString(obj.name).trim();
  if (role === "" && name === "") return nothing;
  return just({ role, name });
}

export function normalizeZwCopyrightPage(raw: unknown): ZwCopyrightPage {
  const base = defaultZwCopyrightPage();
  if (raw === null || typeof raw !== "object") return base;
  const obj = raw as Record<string, unknown>;
  const credits = Array.isArray(obj.credits)
    ? obj.credits.flatMap((c) => {
        const credit = normalizeZwCopyrightCredit(c);
        return credit.tag === "just" ? [credit.value] : [];
      })
    : [];
  return {
    enabled: typeof obj.enabled === "boolean" ? obj.enabled : base.enabled,
    includeRightsParagraph:
      typeof obj.includeRightsParagraph === "boolean"
        ? obj.includeRightsParagraph
        : base.includeRightsParagraph,
    includeAiRestriction:
      typeof obj.includeAiRestriction === "boolean"
        ? obj.includeAiRestriction
        : base.includeAiRestriction,
    editionLine: asString(obj.editionLine),
    publishedBy: asString(obj.publishedBy),
    authorUrl: asString(obj.authorUrl),
    additionalText: asString(obj.additionalText),
    credits,
  };
}

export function normalizeZwDedicationPage(raw: unknown): ZwDedicationPage {
  const base = defaultZwDedicationPage();
  if (raw === null || typeof raw !== "object") return base;
  const obj = raw as Record<string, unknown>;
  return {
    enabled: typeof obj.enabled === "boolean" ? obj.enabled : base.enabled,
    text: asString(obj.text),
  };
}

export function normalizeZwMetadata(raw: unknown): ZwMetadata {
  const base = defaultZwMetadata();
  if (raw === null || typeof raw !== "object") return base;
  const obj = raw as Record<string, unknown>;
  const language = asString(obj.language).trim();
  return {
    title: asString(obj.title),
    subtitle: asString(obj.subtitle),
    author: asString(obj.author),
    language: language !== "" ? language : base.language,
    description: asString(obj.description),
    publisher: asString(obj.publisher),
    rights: asString(obj.rights),
    isbn: asString(obj.isbn),
    lccn: asString(obj.lccn),
    subject: asString(obj.subject),
    pubDate: asString(obj.pubDate),
    coverImage: asString(obj.coverImage),
  };
}

export function normalizeZwStatusBar(raw: unknown): ZwStatusBar {
  const base = defaultZwStatusBar();
  if (raw === null || typeof raw !== "object") return base;
  const obj = raw as Record<string, unknown>;
  return {
    wordCount: obj.wordCount !== false,
    readingTime: obj.readingTime !== false,
    zoom: obj.zoom !== false,
    goals: obj.goals !== false,
    showNeighborScenePeeks: obj.showNeighborScenePeeks === true,
  };
}

function normalizeGoalWords(value: unknown): number | null {
  if (typeof value === "number") {
    if (Number.isFinite(value) === false || value <= 0) return null;
    return Math.floor(value);
  }
  // Some IPC / JSON paths may surface whole numbers as strings.
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value.trim().replace(/,/g, ""));
    if (Number.isFinite(n) === false || n <= 0) return null;
    return Math.floor(n);
  }
  return null;
}

export function normalizeZwGoals(raw: unknown): ZwGoals {
  const base = defaultZwGoals();
  if (raw === null || typeof raw !== "object") return base;
  const obj = raw as Record<string, unknown>;
  return {
    sessionWords: normalizeGoalWords(obj.sessionWords),
    dailyWords: normalizeGoalWords(obj.dailyWords),
    projectWords: normalizeGoalWords(obj.projectWords),
  };
}

function normalizeZwCloudSync(raw: unknown): ZwCloudSync | null {
  if (raw === null || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const lastUploadedOid =
    typeof obj.lastUploadedOid === "string" && obj.lastUploadedOid.trim() !== ""
      ? obj.lastUploadedOid.trim()
      : null;
  const lastPulledOid =
    typeof obj.lastPulledOid === "string" && obj.lastPulledOid.trim() !== ""
      ? obj.lastPulledOid.trim()
      : null;
  let divergence: ZwCloudDivergence | null = null;
  if (obj.divergence !== null && typeof obj.divergence === "object") {
    const d = obj.divergence as Record<string, unknown>;
    const cloudOid =
      typeof d.cloudOid === "string" && d.cloudOid.trim() !== ""
        ? d.cloudOid.trim()
        : "";
    const choice =
      d.choice === "use_cloud"
        ? "use_cloud"
        : d.choice === "keep_local"
          ? "keep_local"
          : null;
    if (cloudOid !== "" && choice !== null) divergence = { cloudOid, choice };
  }
  if (lastUploadedOid === null && lastPulledOid === null && divergence === null)
    return null;
  return { lastUploadedOid, lastPulledOid, divergence };
}

/**
 * Parse one database field definition. Invalid shapes are Err (skipped by
 * `normalizeZwDatabaseFields`); this is the first Result adopter in zwConfig.
 */
export function normalizeZwDatabaseFieldDef(
  raw: unknown,
): Result<ZwDatabaseFieldDef, string> {
  if (raw === null || typeof raw !== "object") {
    return err("database field must be an object");
  }
  const obj = raw as Record<string, unknown>;
  const id = asString(obj.id).trim();
  const name = asString(obj.name).trim();
  const kind =
    typeof obj.kind === "string" && isZwDatabaseFieldKind(obj.kind)
      ? obj.kind
      : null;
  if (id === "") return err("database field requires id");
  if (name === "") return err("database field requires name");
  if (kind === null) return err("database field requires a known kind");
  const options =
    kind === "select" && Array.isArray(obj.options)
      ? obj.options
          .filter((o): o is string => typeof o === "string")
          .map((o) => o.trim())
          .filter((o) => o !== "")
      : [];
  return ok({ id, name, kind, options });
}

export function normalizeZwDatabaseFields(raw: unknown): ZwDatabaseFieldDef[] {
  if (Array.isArray(raw) === false) return [];
  return valuesOk(raw.map(normalizeZwDatabaseFieldDef));
}

/**
 * Parse a `.zwconfig` JSON string into a normalized `ZwConfig`.
 * Invalid JSON is Err. Present-but-invalid schema enums coerce to defaults
 * via `normalizeZwConfig` (mirrors Rust heal-on-read for demo/fixtures with
 * no disk to rewrite).
 */
export function parseZwConfigJson(text: string): Result<ZwConfig, string> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    return err("invalid JSON");
  }
  return ok(normalizeZwConfig(parsed));
}

export function normalizeZwDatabaseRelationPair(
  raw: unknown,
): ZwDatabaseRelationPair | null {
  if (raw === null || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const id = asString(obj.id).trim();
  const aId = asString(obj.aId).trim();
  const aLabel = asString(obj.aLabel).trim();
  const bIdTrimmed = asString(obj.bId).trim();
  const bLabelTrimmed = asString(obj.bLabel).trim();
  const bId = bIdTrimmed !== "" ? bIdTrimmed : aId;
  const bLabel = bLabelTrimmed !== "" ? bLabelTrimmed : aLabel;
  if (id === "" || aId === "" || aLabel === "") return null;
  return { id, aId, aLabel, bId, bLabel };
}

export function normalizeZwDatabaseRelationPairs(
  raw: unknown,
): ZwDatabaseRelationPair[] {
  if (Array.isArray(raw) === false) return [];
  return raw
    .map(normalizeZwDatabaseRelationPair)
    .filter((p): p is ZwDatabaseRelationPair => p !== null);
}

export function normalizeZwConfig(raw: unknown): ZwConfig {
  const base = defaultZwConfig();
  if (raw === null || typeof raw !== "object") return base;
  const obj = raw as Record<string, unknown>;

  // Legacy configs carry a flat `chapters` array and no `manuscript`. The
  // migration lives here so every read path (desktop payload, browser platform,
  // fixtures) gets it without each having to remember.
  const manuscript = normalizeManuscript(obj.manuscript, obj.chapters);

  const fontsRaw =
    obj.fonts !== null && typeof obj.fonts === "object"
      ? (obj.fonts as Record<string, unknown>)
      : {};
  const body =
    typeof fontsRaw.body === "string" && isEditorFontId(fontsRaw.body)
      ? fontsRaw.body
      : DEFAULT_BODY_FONT;
  const heading =
    typeof fontsRaw.heading === "string" && isEditorFontId(fontsRaw.heading)
      ? fontsRaw.heading
      : DEFAULT_HEADING_FONT;

  const printSize =
    typeof obj.printSize === "string" && isZwPrintSize(obj.printSize)
      ? obj.printSize
      : DEFAULT_PRINT_SIZE;

  const dictionary = Array.isArray(obj.dictionary)
    ? [
        ...new Set(
          obj.dictionary
            .filter((w): w is string => typeof w === "string")
            .map((w) => w.trim())
            .filter((o) => o !== ""),
        ),
      ]
    : [];

  const cloudProjectId =
    typeof obj.cloudProjectId === "string" && obj.cloudProjectId.trim() !== ""
      ? obj.cloudProjectId.trim()
      : null;

  const cloudSync = normalizeZwCloudSync(obj.cloudSync);

  // Legacy single switch → all on/off; prefer explicit `statusBar` when present.
  let statusBar = normalizeZwStatusBar(obj.statusBar);
  if (obj.statusBar === null && obj.showStatusBar === false) {
    statusBar = {
      wordCount: false,
      readingTime: false,
      zoom: false,
      goals: false,
      showNeighborScenePeeks: false,
    };
  }
  // #264 briefly lived under sceneFormatting; lift into statusBar when
  // the interface field is still unset.
  const statusBarRaw =
    obj.statusBar !== null && typeof obj.statusBar === "object"
      ? (obj.statusBar as Record<string, unknown>)
      : null;
  const sceneFormattingRaw =
    obj.sceneFormatting !== null && typeof obj.sceneFormatting === "object"
      ? (obj.sceneFormatting as Record<string, unknown>)
      : null;
  if (
    statusBarRaw !== null &&
    Object.prototype.hasOwnProperty.call(
      statusBarRaw,
      "showNeighborScenePeeks",
    ) === false &&
    sceneFormattingRaw?.showNeighborScenePeeks === true
  ) {
    statusBar = { ...statusBar, showNeighborScenePeeks: true };
  }
  if (
    statusBarRaw === null &&
    sceneFormattingRaw?.showNeighborScenePeeks === true
  ) {
    statusBar = { ...statusBar, showNeighborScenePeeks: true };
  }

  return {
    version: 1,
    manuscript,
    fonts: { body, heading },
    printSize,
    paragraphFormatting: normalizeZwParagraphFormatting(
      obj.paragraphFormatting,
    ),
    chapterHeadingStyle: normalizeZwChapterHeadingStyle(
      obj.chapterHeadingStyle,
    ),
    pageChrome: normalizeZwPageChrome(obj.pageChrome),
    chapterNumbering: normalizeChapterNumbering(obj.chapterNumbering),
    actNumbering: normalizeChapterNumbering(obj.actNumbering),
    chapterNumberingOverrides: normalizeChapterOverrides(
      obj.chapterNumberingOverrides,
    ),
    sceneFormatting: normalizeZwSceneFormatting(obj.sceneFormatting),
    copyrightPage: normalizeZwCopyrightPage(obj.copyrightPage),
    dedicationPage: normalizeZwDedicationPage(obj.dedicationPage),
    metadata: normalizeZwMetadata(obj.metadata),
    dictionary,
    cloudProjectId,
    ...(cloudSync !== null ? { cloudSync } : {}),
    statusBar,
    goals: normalizeZwGoals(obj.goals),
    databaseFields: normalizeZwDatabaseFields(obj.databaseFields),
    databaseRelationPairs: normalizeZwDatabaseRelationPairs(
      obj.databaseRelationPairs,
    ),
  };
}
